import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronUp, Pencil, Play, Plus, Save, Trash2, Users } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { getStoredUser } from "@/lib/auth";
import { queryKeys } from "@/lib/queryClient";
import { useTenant } from "@/contexts/TenantContext";

import { usePageTitle } from "@/contexts/PageTitleContext";
import SoldDialog from "@/components/appointments/SoldDialog";
import FireworksCanvas from "@/components/appointments/FireworksCanvas";
import { DetailPageSkeleton } from "@/components/skeletons/DetailPageSkeleton";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import SectionCard from "@/pages/appointmentDetails/SectionCard";
import AppointmentQuotesSection from "@/pages/appointmentDetails/AppointmentQuotesSection";
import AppointmentDiscoverySection from "@/pages/appointmentDetails/AppointmentDiscoverySection";
import AppointmentProspectDetailsSection from "@/pages/appointmentDetails/AppointmentProspectDetailsSection";
import AppointmentHistorySection from "@/pages/appointmentDetails/AppointmentHistorySection";
import AppointmentLeadDetailsSection from "@/pages/appointmentDetails/AppointmentLeadDetailsSection";
import AddQuoteDialog from "@/pages/appointmentDetails/AddQuoteDialog";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import AddProspectDialog from "@/components/appointments/AddProspectDialog";
import SetAppointmentDialog from "@/components/appointments/SetAppointmentDialog";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";

// Mirrors the server's `APPOINTMENT_REASSIGNMENT_ROLES`. Presentation only --
// `appointment_actor_assignment.require_reassignment_capability` is the gate.
const APPOINTMENT_REASSIGNMENT_ROLES = ["owner", "admin", "customer_service_agent", "manager"];
import AppointmentSummaryDialog from "@/components/appointments/AppointmentSummaryDialog";

import StartRunScriptDialog from "@/components/scripts/StartRunScriptDialog";


import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import SingleSelectFilter from "@/components/leads/SingleSelectFilter";
import { getQuoteFieldLabel, isCompanyQuoteField, isIssueAgeQuoteField, isProductQuoteField } from "@/lib/quoteFieldUtils";
import { getBreadcrumbBackPath } from "@/lib/breadcrumbs";
import { buildPathWithCurrentTenant } from "@/lib/tenantPath";

import {
  fmtDateTime,
  statusPill,
  prospectDetailsDefaults,
  formatDobValue,
  normalizeDateInput,
  normalizeInsuranceType,
  pickQuoteRowValue,
} from "@/pages/appointmentDetails/helpers";


export default function AppointmentDetails() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { effectiveModules, userRole } = usePermissions(); // MA-ARCH-201 Phase 3: Permission resolution

  // P1 HARDENING (A3): Invalidate both list AND detail queries after mutations
  const invalidateAppointmentsCache = useCallback(() => {
    // Invalidate list queries
    queryClient.invalidateQueries({
      queryKey: queryKeys.appointments.all(selectedTenantId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.appointments.pendingSummaries(selectedTenantId),
    });
    // Invalidate the specific detail query for this appointment
    if (appointmentId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.detail(selectedTenantId, appointmentId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.quotes(selectedTenantId, appointmentId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.discovery(selectedTenantId, appointmentId),
      });
    }
  }, [queryClient, selectedTenantId, appointmentId]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appt, setAppt] = useState(null);
  const [quotes, setQuotes] = useState({ prospects: [], runs: [] });
  const [history, setHistory] = useState([]);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  // Which Appointment the follow-up scheduler is booking a follow-up OF.
  // `null` means the route Appointment.
  //
  // Two different intentions open that one dialog and they do NOT share a
  // parent. The section-level "+" means "book another Appointment for this
  // prospect" from the page you are standing on, so its parent is the route
  // Appointment. Answering Yes on an Appointment Summary means "book the next
  // Appointment after THIS one", and the Summary is row-bound -- it can be any
  // Appointment in the history, not just the route's.
  //
  // Both read `appointmentId` before, so summarising a non-route row and
  // answering Yes posted the follow-up against whichever Appointment the URL
  // happened to name: B became a follow-up of the Appointment being viewed
  // rather than the one just summarised, and under #637 inherited that one's
  // actors too. Same defect shape #619 removed from the section-level
  // Reschedule, surviving on this path because the two openers were
  // indistinguishable once the dialog was open.
  //
  // Held as an id rather than the row: the dialog needs identity only, and
  // `summaryAppt` is cleared during onSubmitted, before the scheduler opens.
  const [followUpParentId, setFollowUpParentId] = useState(null);
  const [discovery, setDiscovery] = useState({ run_id: null, run_at: null, script_name: null, prospects: [], fields: [], values: [] });
  const [clientCreateDate, setClientCreateDate] = useState(null);
  const [firstAppointmentCreatedAt, setFirstAppointmentCreatedAt] = useState(null);

  const [soldOpen, setSoldOpen] = useState(false);
  const [soldContext, setSoldContext] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [activeProspectId, setActiveProspectId] = useState(null);
  const [householdLeads, setHouseholdLeads] = useState([]);
  const [addProspectOpen, setAddProspectOpen] = useState(false);
  // The Appointment the reschedule dialog is acting on, or null when it is
  // closed. This used to be a bare `apptOpen` boolean, which forced the dialog
  // to fall back to the route's appointmentId -- so with several Appointments
  // in the history, Reschedule always moved the one named in the URL rather
  // than the one the user picked. Holding the row itself makes the target
  // explicit and removes that fallback entirely.
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryAppt, setSummaryAppt] = useState(null);
  const [isProspectEditing, setIsProspectEditing] = useState(false);
  const [isDiscoveryEditing, setIsDiscoveryEditing] = useState(false);
  const [prospectContactOverrides, setProspectContactOverrides] = useState({});
  const [isProspectSaving, setIsProspectSaving] = useState(false);
  const [discoveryEdits, setDiscoveryEdits] = useState({});
  const [latestOverridesByKey, setLatestOverridesByKey] = useState({});
  const [openQuoteProspectId, setOpenQuoteProspectId] = useState(null);
  const [manualQuotes, setManualQuotes] = useState([]);
  const [quoteTemplateOpen, setQuoteTemplateOpen] = useState(false);
  const [quoteTemplateOption, setQuoteTemplateOption] = useState("mortgage-protection");
  const [quoteTemplates, setQuoteTemplates] = useState([]);
  const [quoteTemplateFields, setQuoteTemplateFields] = useState([]);
  const saveSelectTriggerClass = "h-9 rounded-full bg-indigo-500/20 text-sm text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30";

  const [quoteTemplateLoading, setQuoteTemplateLoading] = useState(false);
  const [discoveryProspectId, setDiscoveryProspectId] = useState(null);
  const [discoveryRuns, setDiscoveryRuns] = useState([]);

  // (removed) activeDiscoverySubject toggle; Prospect/Household remain as collapsible sections

  const [selectedDiscoveryRunId, setSelectedDiscoveryRunId] = useState("all");
  const [discoverySectionsOpen, setDiscoverySectionsOpen] = useState({ household: false, prospects: false });
  const [quotesEditMode, setQuotesEditMode] = useState(false);
  const [selectedQuoteProspectId, setSelectedQuoteProspectId] = useState("prospect-1");

  const [appointmentsEditMode, setAppointmentsEditMode] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [isQuotesSaving, setIsQuotesSaving] = useState(false);
  const [isDiscoverySaving, setIsDiscoverySaving] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);

  const [quotesDirty, setQuotesDirty] = useState(false);
  const [discoveryDirty, setDiscoveryDirty] = useState(false);


  const segmentedToggleWrapper =
    "inline-flex h-auto w-fit max-w-full flex-wrap items-center gap-1 rounded-full bg-indigo-500/20 p-1 text-indigo-100 ring-1 ring-indigo-400/20";
  const segmentedToggleBase = "rounded-full px-3 py-1 text-xs font-semibold text-indigo-100/80 hover:text-indigo-100";
  const segmentedToggleActive = "bg-indigo-500/30 text-indigo-100";

  // (removed) discovery subject toggle styles
  const accentIconButtonClass =
    "h-9 w-9 rounded-full bg-indigo-500/20 p-0 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30";
  const saveIconButtonClass =
    "h-9 w-9 rounded-full bg-amber-500/20 p-0 text-amber-100 ring-1 ring-amber-400/30 hover:bg-amber-500/30";

  const activeProspectRef = useRef(activeProspectId);
  const discoveryProspectRef = useRef(discoveryProspectId);
  useEffect(() => {
    activeProspectRef.current = activeProspectId;
  }, [activeProspectId]);

  const [scriptPickerOpen, setScriptPickerOpen] = useState(false);

  const lead = appt?.lead_snapshot || {};
  const user = useMemo(() => getStoredUser(), []);
  const soldPayloads = useMemo(() => {
    const payloads = Array.isArray(appt?.sold_payloads) ? appt.sold_payloads : [];
    if (payloads.length) return payloads;
    if (appt?.sold_payload) return [appt.sold_payload];
    return [];
  }, [appt?.sold_payload, appt?.sold_payloads]);

  const hiddenQuotePanels = useMemo(() => (Array.isArray(appt?.hidden_quote_panels) ? appt.hidden_quote_panels : []), [appt?.hidden_quote_panels]);

  const deleteTargetLabel = useMemo(() => {
    if (!deleteTarget) return "item";
    return deleteTarget.kind === "appointment" ? "appointment" : "quote";
  }, [deleteTarget]);

  const leadLabel = useMemo(() => {
    const parts = [lead?.first_name, lead?.last_name].filter(Boolean);
    return parts.join(" ") || lead?.full_name || lead?.name || lead?.email || "Prospect";
  }, [lead]);
  const leadForScheduling = useMemo(() => ({ ...lead, id: appt?.lead_id }), [lead, appt?.lead_id]);

  const title = useMemo(() => {
    return lead.full_name || appt?.title || "Appointment";
  }, [lead.full_name, appt?.title]);

  usePageTitle(title || "Appointment Details");

  const activeProspect = useMemo(() => prospects.find((p) => p.id === activeProspectId) || null, [prospects, activeProspectId]);
  const activeProspectIndex = useMemo(() => {
    if (!prospects.length) return 0;
    const index = prospects.findIndex((p) => p.id === activeProspectId);
    return index >= 0 ? index : 0;
  }, [prospects, activeProspectId]);

  const hasProspectToggle = prospects.length > 1;
  const primaryProspect = prospects[0];
  const secondaryProspect = prospects[1];

  const displayLead = useMemo(() => {
    if (!activeProspect) return lead;
    const leadId = activeProspect?.lead_id;
    if (leadId) {
      return householdLeads.find((x) => x.id === leadId) || lead;
    }

    // Fallback: if Prospect 2 doesn't have lead_id mapped yet, try matching by name.
    const name = String(activeProspect?.name || "").trim().toLowerCase();
    if (name) {
      const byName = householdLeads.find((x) => String(x.full_name || "").trim().toLowerCase() === name);
      if (byName) return byName;
    }

    return null;
  }, [activeProspect, householdLeads, lead]);

  const canDeleteAppointment = useCallback(
    (item) => {
      if (!item) return false;
      // MA-ARCH-201 Phase 3: Use membership-based permissions (not user.role)
      // Appointments are part of lead management, check "leads" module access
      const hasDeletePermission = effectiveModules.includes("leads");
      const isOwner = Boolean(item.created_by_user_id && item.created_by_user_id === user?.id);
      return hasDeletePermission || isOwner;
    },
    [effectiveModules, user?.id]
  );

  const baseProspectContact = useMemo(() => {
    // Always derive defaults from the currently selected prospect's lead (displayLead).
    // This ensures Prospect 2 edits persist and rehydrate correctly after refresh.
    const leadDefaults = displayLead || {};
    return {
      name: leadDefaults?.full_name || activeProspect?.name || leadLabel || "",
      phone: leadDefaults?.phone_number || leadDefaults?.phone || "",
      email: leadDefaults?.email || "",
      address: leadDefaults?.address || "",
      city: leadDefaults?.city || "",
      state: leadDefaults?.state || "",
      zip: leadDefaults?.zip || "",
      county: leadDefaults?.county || "",
      dob: normalizeDateInput(leadDefaults?.dob || ""),
    };
  }, [activeProspect?.name, displayLead, leadLabel]);

  const activeProspectContact = useMemo(
    () => ({ ...baseProspectContact, ...(prospectContactOverrides[activeProspectId] || {}) }),
    [baseProspectContact, prospectContactOverrides, activeProspectId]
  );

  const updateProspectContact = (field, value) => {
    if (!activeProspectId) return;

    setProspectContactOverrides((prev) => ({
      ...prev,
      [activeProspectId]: {
        ...(prev[activeProspectId] || {}),
        [field]: value,
      },
    }));
  };

  const ensureSecondaryLead = async () => {
    if (!appt?.household_id) return null;
    if (!activeProspectContact?.name) return null;

    try {
      // If already mapped, nothing to do
      if (activeProspect?.lead_id) return activeProspect.lead_id;

      // Ensure we have the latest household leads to avoid creating duplicates.
      let leads = householdLeads || [];
      if (appt?.household_id) {
        try {
          const hl = await api.get(`/households/${appt.household_id}/leads`);
          leads = hl.data?.items || leads;
          setHouseholdLeads(leads);
        } catch {
          // ignore
        }
      }

      // If a household lead already exists by name, reuse it instead of creating duplicates.
      const name = String(activeProspectContact.name || "").trim().toLowerCase();
      const matches = (leads || []).filter((l) => String(l.full_name || "").trim().toLowerCase() === name);
      const existing = matches.length
        ? matches.reduce((best, cur) => {
            const bestDob = best?.dob ? String(best.dob) : "";
            const curDob = cur?.dob ? String(cur.dob) : "";
            if (curDob && !bestDob) return cur;
            if (bestDob && !curDob) return best;

            const bts = Date.parse(best?.updated_at || best?.created_at || "") || 0;
            const cts = Date.parse(cur?.updated_at || cur?.created_at || "") || 0;
            return cts >= bts ? cur : best;
          }, matches[0])
        : null;
      if (existing?.id) {
        const existingId = existing.id;
        setProspects((prev) => (prev || []).map((p) => (p.id === activeProspectId ? { ...p, lead_id: existingId } : p)));
        // Ensure household leads include this lead as well (deduped)
        setHouseholdLeads((prev) => {
          const next = [...(prev || [])];
          if (!next.some((l) => l.id === existingId)) next.push(existing);
          return next;
        });
        return existingId;
      }

      const res = await api.post(`/households/${appt.household_id}/leads`, {
        full_name: activeProspectContact.name,
        phone_number: activeProspectContact.phone || null,
        email: activeProspectContact.email || null,
      });

      const newLeadId = res.data?.id;
      if (!newLeadId) return null;

      // Update local prospect mapping
      setProspects((prev) =>
        (prev || []).map((p) => (p.id === activeProspectId ? { ...p, lead_id: newLeadId } : p))
      );
      setHouseholdLeads((prev) => [...(prev || []), res.data]);
      return newLeadId;
    } catch {
      return null;
    }
  };

  const toggleProspect = (checked) => {
    if (!hasProspectToggle) return;
    const next = checked ? secondaryProspect : primaryProspect;
    if (next?.id) {
      setActiveProspectId(next.id);
    }
  };

  const discoveryProspect = useMemo(
    () => prospects.find((p) => p.id === discoveryProspectId) || null,
    [prospects, discoveryProspectId]
  );

  const discoveryProspectIndex = useMemo(() => {
    if (!prospects.length) return 0;
    const index = prospects.findIndex((p) => p.id === discoveryProspectId);
    return index >= 0 ? index : 0;
  }, [prospects, discoveryProspectId]);

  const toggleDiscoveryProspect = (checked) => {
    if (!hasProspectToggle) return;
    const next = checked ? secondaryProspect : primaryProspect;
    if (next?.id) {
      setDiscoveryProspectId(next.id);
    }
  };

  const discoveryValuesByKey = useMemo(() => {
    const map = new Map();

    // Base values from run_data
    for (const v of discovery.values || []) {
      const key = `${v.field_id}::${v.subject_id || "household"}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(v);
    }

    // Overlay appointment-level overrides when in "All Runs" view.
    if (selectedDiscoveryRunId === "all") {
      Object.entries(latestOverridesByKey || {}).forEach(([k, vals]) => {
        const parts = String(k).split("::");
        const subjectId = parts[0];
        const fieldKey = parts[1];
        if (!fieldKey) return;
        map.set(`${fieldKey}::${subjectId || "household"}`, (vals || []).map((v) => ({ value: v })));
      });
    }

    return map;
  }, [discovery.values, latestOverridesByKey, selectedDiscoveryRunId]);

  const discoveryRunOptions = useMemo(() => {
    const base = discoveryRuns.length
      ? discoveryRuns
      : discovery?.run_id
        ? [{ id: discovery.run_id, script_name: discovery.script_name, run_at: discovery.run_at }]
        : [];
    return [{ id: "all", script_name: "All Runs" }, ...base];
  }, [discoveryRuns, discovery]);

  const discoveryFields = useMemo(() => {
    const fields = Array.isArray(discovery?.fields) ? discovery.fields : [];
    const household = fields.filter((f) => f?.scope !== "prospects");
    const prospectsFields = fields.filter((f) => f?.scope === "prospects");
    return { household, prospects: prospectsFields };
  }, [discovery]);

  const prospectDob = useMemo(() => {
    const leadDob = displayLead?.dob || displayLead?.date_of_birth || displayLead?.birthdate;
    const fallbackDob = activeProspectContact?.dob;
    if (fallbackDob) {
      return formatDobValue(fallbackDob);
    }
    const allFields = Array.isArray(discovery?.fields) ? discovery.fields : [];
    const dobField = allFields.find((field) => {
      const label = field?.label || field?.name || field?.key || "";
      return /dob|date of birth/i.test(label);
    });
    if (!dobField) {
      return formatDobValue(leadDob || fallbackDob);
    }
    const fieldId = dobField.id || dobField.field_id || dobField.key;
    const subjectId = activeProspect?.id || "household";
    const leadSubjectId = activeProspect?.lead_id;
    const directKey = `${fieldId}::${subjectId}`;
    const leadKey = leadSubjectId ? `${fieldId}::${leadSubjectId}` : null;
    const values =
      discoveryValuesByKey.get(directKey) ||
      (leadKey ? discoveryValuesByKey.get(leadKey) : null) ||
      discoveryValuesByKey.get(`${fieldId}::household`) ||
      [];
    const rawValue = values[0]?.value ?? values[0];
    return formatDobValue(rawValue ?? leadDob ?? fallbackDob);
  }, [activeProspect?.id, activeProspect?.lead_id, discovery, discoveryValuesByKey, displayLead, activeProspectContact?.dob]);

  const formatDiscoveryValue = (field, values) => {
    const normalizeValue = (value) => {
      if (value === null || value === undefined) return "";
      if (Array.isArray(value)) {
        return value.map(normalizeValue).filter(Boolean).join(", ");
      }
      if (typeof value === "object") {
        if (value.value !== undefined) return normalizeValue(value.value);
        if (value.label) return String(value.label);
        if (value.name) return String(value.name);
        return JSON.stringify(value);
      }
      return String(value);
    };

    const clean = (values || []).map((v) => normalizeValue(v).trim()).filter(Boolean);
    if (clean.length === 0) return "—";
    if (field?.field_type === "checkbox") {
      return clean.map((v) => (String(v).toLowerCase() === "true" ? "Yes" : "No")).join(", ");
    }
    return clean.join(", ");
  };

  const appointmentHistory = useMemo(() => (Array.isArray(history) ? history : []), [history]);

  const quoteProspectOptions = useMemo(() => {
    const discPros = Array.isArray(discovery?.prospects) ? discovery.prospects : [];
    const runPros = Array.isArray(prospects) ? prospects : [];

    const merged = new Map();

    // Prefer discovery prospect IDs (these represent the real prospect objects)
    discPros.forEach((p) => {
      if (!p?.id) return;
      merged.set(p.id, { id: p.id, name: p.name || "Prospect", lead_id: p.lead_id || null });
    });

    // Add any missing prospects from other sources
    runPros.forEach((p) => {
      const pid = p?.id;
      if (!pid) return;
      if (!merged.has(pid)) merged.set(pid, { id: pid, name: p.name || "Prospect", lead_id: p.lead_id || null });
    });

    // De-dupe by display name so Spencer Smith doesn't show twice if IDs differ.
    const byName = new Map();
    Array.from(merged.values()).forEach((p) => {
      const key = String(p.name || "").trim().toLowerCase();
      if (!key) return;
      if (!byName.has(key)) byName.set(key, p);
    });

    // Fallback only if we truly have no prospects
    if (!byName.size) {
      byName.set("prospect 1", { id: "prospect-1", name: lead?.full_name || "Prospect 1", lead_id: appt?.lead_id || null });
    }

    return Array.from(byName.values());
  }, [appt?.lead_id, discovery?.prospects, lead?.full_name, prospects]);

  const quoteProspectLeadMap = useMemo(() => {
    const map = new Map();
    quoteProspectOptions.forEach((p) => {
      if (p?.id) map.set(p.id, p.lead_id || null);
    });
    return map;
  }, [quoteProspectOptions]);

  const selectedQuoteProspect = useMemo(
    () => quoteProspectOptions.find((p) => p.id === selectedQuoteProspectId) || quoteProspectOptions[0],
    [quoteProspectOptions, selectedQuoteProspectId]
  );

  const selectedQuoteProspectName = selectedQuoteProspect?.name || leadLabel;
  const selectedQuoteLeadId = selectedQuoteProspect?.lead_id || displayLead?.id || appt?.lead_id || null;

  // With per-manual-quote panels, edits should target the quote's own id.
  const quoteProspectToManualQuoteId = useMemo(() => ({}), []);

  const selectedTemplate = useMemo(() => {
    if (!quoteTemplateOption?.startsWith?.("template:")) return null;
    const id = quoteTemplateOption.split(":")[1];
    return (quoteTemplates || []).find((t) => t.id === id) || null;
  }, [quoteTemplateOption, quoteTemplates]);


  const quoteProspects = useMemo(() => {
    const runQuotes = Array.isArray(quotes?.prospects) ? quotes.prospects : [];

    // Keep manual quotes as separate panels (do not group by prospect) so accordion open state
    // affects only the clicked panel.
    const manualPanels = (manualQuotes || []).filter((q) => q && q.is_manual);

    const runPanels = runQuotes.map((p) => ({ ...p, _panel_kind: "run" }));
    const manualWithKind = manualPanels.map((p) => ({ ...p, _panel_kind: "manual" }));

    return [...manualWithKind, ...runPanels].filter(Boolean);
  }, [manualQuotes, quotes]);

  const buildSoldPrefill = useCallback((quotePanel) => {
    const rows = Array.isArray(quotePanel?.quote_rows) ? quotePanel.quote_rows : [];
    const insuranceTypeRaw = pickQuoteRowValue(rows, [/insurance type/i, /product type/i]);
    const insuranceType = normalizeInsuranceType(insuranceTypeRaw) || normalizeInsuranceType(quotePanel?.playbook_name || "");
    const company = String(pickQuoteRowValue(rows, [/company/i, /carrier/i]) || "").trim();
    const productName = String(pickQuoteRowValue(rows, [/product/i]) || "").trim();
    const issueAge = String(pickQuoteRowValue(rows, [/issue age/i]) || "").trim();
    const monthlyPremium = String(pickQuoteRowValue(rows, [/monthly premium/i]) || "").trim();
    const deathBenefit = String(pickQuoteRowValue(rows, [/death benefit/i]) || "").trim();
    return {
      insurance_type: insuranceType,
      company,
      product_name: productName,
      issue_age: issueAge,
      monthly_premium: monthlyPremium,
      death_benefit: deathBenefit,
      sales_agent_id: appt?.sales_agent_user_id || null,
    };
  }, []);

  // P4-DEBT-B: the actor-option lists are gone with the selectors they fed.
  // They were built here only for AppointmentProspectDetailsSection's Sales and
  // Booking pickers; this page's remaining actor surface is WP-14C Reassign,
  // which takes its candidates from `GET /appointments/assignable-actors`. The
  // shared eligibility predicates live on in `@/lib/appointmentActors` for
  // SetAppointmentDialog, which is a CREATION path and a legitimate place to
  // choose actors.

  const loadAppointment = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/appointments/${appointmentId}`);
      setAppt(res.data);
      const appointmentProspectMap = new Map((res.data?.prospects || []).map((p) => [p.id, p]));

      let qProspects = [];
      let discoveryProspectsList = [];

      try {

        const qRes = await api.get(`/appointments/${appointmentId}/quotes`);

        // Load any saved manual quotes for this appointment
        try {
          const mRes = await api.get(`/appointments/${appointmentId}/manual_quotes`);
          const manual = Array.isArray(mRes.data) ? mRes.data : [];
          setManualQuotes(
            manual.map((mq) => ({
              is_manual: true,
              id: mq.id,
              prospect_id: mq.prospect_id,
              prospect_name: mq.prospect_name,
              lead_id: mq.lead_id,
              playbook_name: mq.playbook_name,
              created_at: mq.created_at,
              quote_rows: mq.quote_rows || [],
            }))
          );
        } catch {
          // ignore
        }

        setQuotes(qRes.data || { prospects: [], runs: [] });
        qProspects = (qRes.data?.prospects || []).map((p) => ({ id: p.prospect_id, name: p.prospect_name, lead_id: p.lead_id || null }));

        // Lead mapping will be merged from the main discovery payload below.
      } catch (quoteError) {
        console.warn("Quotes not available", quoteError);
        setQuotes({ prospects: [], runs: [] });
      }


      // Load saved Quote Templates for the Quote Type dropdown.
      //
      // P4 Quote Templates frontend lifecycle: this loader is READ-ONLY. An
      // administrator's explicit Quote Template configuration is authoritative,
      // so "Mortgage Protection" is not a mandatory system template and is never
      // created here. An Account may legitimately hold zero Quote Templates; a
      // deleted template stays deleted; a renamed one is not resurrected. The
      // only creation path is explicit user action (QuoteTemplateBuilder).
      try {
        const tRes = await api.get("/quote-templates");
        const templates = Array.isArray(tRes.data) ? tRes.data : [];

        // Selection only, never creation: resolve the legacy
        // "mortgage-protection" sentinel to a real template when the Account
        // happens to have one. Absence is a valid state and is left alone.
        const mortgageTemplate = templates.find(
          (t) => String(t?.name || "").trim().toLowerCase() === "mortgage protection"
        );
        setQuoteTemplates(templates);
        if (mortgageTemplate) {
          setQuoteTemplateOption((prev) => (prev === "mortgage-protection" ? `template:${mortgageTemplate.id}` : prev));
        }
      } catch {
        setQuoteTemplates([]);
      }

      // Load discovery overrides (appointment-level) for the selected run.
      // Disabled for "All Runs" in the UI; for All Runs view we later use latest-run overrides.
      try {
        const rid = selectedDiscoveryRunId && selectedDiscoveryRunId !== "all" ? selectedDiscoveryRunId : null;
        if (rid) {
          const oRes = await api.get(`/appointments/${appointmentId}/discovery_overrides`, { params: { run_id: rid } });
          const items = Array.isArray(oRes.data?.items) ? oRes.data.items : [];
          setDiscoveryEdits(
            items.reduce((acc, it) => {
              const key = `${it.subject_id || "household"}::${it.field_key}`;
              acc[key] = Array.isArray(it.values) ? it.values : [];
              return acc;
            }, {})
          );


        }
      } catch {
        // ignore
      }


      const normalizedQProspects = qProspects.map((item) => {
        const saved = appointmentProspectMap.get(item.id);
        if (!saved) return item;
        return {
          ...item,
          ...saved,
          name: saved.name ?? item.name,
          lead_id: saved.lead_id ?? item.lead_id,
        };
      });
      setProspects(normalizedQProspects);

      let dRes = { data: { runs: [], prospects: [] } };
      try {
        dRes = await api.get(`/appointments/${appointmentId}/discovery`, {
          params: selectedDiscoveryRunId ? { run_id: selectedDiscoveryRunId } : {},
        });
        setDiscovery(dRes.data || { run_id: null, run_at: null, script_name: null, prospects: [], fields: [], values: [] });
        setDiscoveryRuns(dRes.data?.runs || []);
        discoveryProspectsList = (dRes.data?.prospects || []).map((p) => ({ id: p.id, name: p.name, lead_id: p.lead_id }));
      } catch (discoveryError) {
        console.warn("Discovery not available", discoveryError);
        setDiscovery({ run_id: null, run_at: null, script_name: null, prospects: [], fields: [], values: [] });
        setDiscoveryRuns([]);
      }

      const leadSnapshot = res.data?.lead_snapshot || {};
      const leadDefaults = prospectDetailsDefaults(leadSnapshot);
      const fallbackProspect = leadDefaults?.name
        ? {
            id: "lead-default",
            ...leadDefaults,
          }
        : null;

      // Load latest-run discovery overrides so All Runs can display them (without editing).
      try {
        const latestRunId = (dRes.data?.runs || [])[0]?.id;
        if (latestRunId) {
          const oResLatest = await api.get(`/appointments/${appointmentId}/discovery_overrides`, { params: { run_id: latestRunId } });
          const itemsLatest = Array.isArray(oResLatest.data?.items) ? oResLatest.data.items : [];
          setLatestOverridesByKey(
            itemsLatest.reduce((acc, it) => {
              const key = `${it.subject_id || "household"}::${it.field_key}`;


              acc[key] = Array.isArray(it.values) ? it.values : [];
              return acc;
            }, {})
          );
        }
      } catch {
        // ignore
      }

      if (discoveryProspectsList.length) {
        const merged = new Map();
        discoveryProspectsList.forEach((p) => merged.set(p.id, p));
        qProspects.forEach((p) => {
          const existing = merged.get(p.id);
          merged.set(p.id, existing ? { ...p, ...existing } : p);
        });
        const mergedList = Array.from(merged.values());
        const normalizedList = mergedList.map((item) => {
          const saved = appointmentProspectMap.get(item.id);
          if (!saved) return item;
          return {
            ...item,
            ...saved,
            name: saved.name ?? item.name,
            lead_id: saved.lead_id ?? item.lead_id,
          };
        });
        setProspects(normalizedList);
        if (!activeProspectRef.current) setActiveProspectId(normalizedList[0]?.id || null);
        if (!discoveryProspectRef.current) setDiscoveryProspectId(normalizedList[0]?.id || null);
      } else if (fallbackProspect) {
        setProspects([fallbackProspect]);
        if (!activeProspectRef.current) setActiveProspectId(fallbackProspect.id);
        if (!discoveryProspectRef.current) setDiscoveryProspectId(fallbackProspect.id);
      } else if (!activeProspectRef.current && qProspects.length) {
        setActiveProspectId(qProspects[0].id);
        if (!discoveryProspectRef.current) setDiscoveryProspectId(qProspects[0].id);
      }

      // appointment history for the same lead (includes follow-ups)
      const leadId = res.data?.lead_id;
      if (leadId) {
        const hRes = await api.get("/appointments", {
          params: {
            filters: JSON.stringify([{ field: "lead_id", operator: "equals", value: leadId }]),
            sort_by: "start_at",
            sort_dir: "desc",
            limit: 50,
            offset: 0,
          },
        });
        setHistory(hRes.data?.items || []);
      } else {
        setHistory([]);
      }

      // household leads for prospect switching
      if (res.data?.household_id) {
        const hl = await api.get(`/households/${res.data.household_id}/leads`);
        const items = hl.data?.items || [];

        // De-dupe by full_name (keep most recently updated) to avoid Prospect 2 mapping instability.
        const byName = new Map();
        items.forEach((l) => {
          const key = String(l.full_name || "").trim().toLowerCase();
          if (!key) return;
          const prev = byName.get(key);
          if (!prev) {
            byName.set(key, l);
            return;
          }
          const prevTs = Date.parse(prev.updated_at || prev.created_at || "") || 0;
          const nextTs = Date.parse(l.updated_at || l.created_at || "") || 0;
          byName.set(key, nextTs >= prevTs ? l : prev);
        });

        setHouseholdLeads(Array.from(byName.values()));
      } else {
        setHouseholdLeads([]);
      }
    } catch (error) {
      console.error(error);
      setError("Failed to load appointment.");
      setAppt(null);
      setQuotes({ prospects: [], runs: [] });
      setDiscovery({ run_id: null, run_at: null, script_name: null, prospects: [], fields: [], values: [] });
      setDiscoveryRuns([]);
      setHistory([]);
      setHouseholdLeads([]);
    } finally {
      setLoading(false);
    }
  }, [appointmentId, selectedDiscoveryRunId]);

  // =====================================================================
  // P4-WP-14C: explicit Appointment actor reassignment.
  //
  // PRESENTATION GATING ONLY. `usePermissions` already resolves the caller's
  // membership role for the SELECTED Account from `/auth/me/access-context`,
  // so the action can be hidden locally without issuing a request that is
  // expected to be forbidden. An earlier draft discovered the capability by
  // calling the assignee endpoint and reading 403 as "no" -- that worked, but
  // it meant every Sales and Booking Agent generated a deliberate 403 on
  // every Appointment they opened, which is noise in the console, the network
  // panel and any error telemetry watching for 4xx.
  //
  // This is NOT the security boundary. The backend re-checks the capability on
  // both the assignee projection and the mutation, and a crafted request from
  // a role without it is still 403. The role is read here only to decide
  // whether to draw a button.
  // =====================================================================
  const canReassign = APPOINTMENT_REASSIGNMENT_ROLES.includes(userRole);
  const [assignableActors, setAssignableActors] = useState({ sales: [], booking: [] });
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignSalesId, setReassignSalesId] = useState("");
  const [reassignBookingId, setReassignBookingId] = useState("");
  const [reassignSaving, setReassignSaving] = useState(false);

  const openReassign = useCallback(async () => {
    // Seed from what the user is looking at; these become the `from` values,
    // which is what makes the compare-and-set meaningful.
    setReassignSalesId(appt?.sales_agent_user_id || "");
    setReassignBookingId(appt?.booking_agent_user_id || "");
    setReassignOpen(true);
    // Fetched on open, not on page load: only a user who is actually
    // reassigning needs the list, and only an authorized role can reach this.
    try {
      const res = await api.get("/appointments/assignable-actors");
      setAssignableActors({ sales: res.data?.sales || [], booking: res.data?.booking || [] });
    } catch {
      setAssignableActors({ sales: [], booking: [] });
    }
  }, [appt?.sales_agent_user_id, appt?.booking_agent_user_id]);

  const submitReassign = useCallback(async () => {
    const currentSales = appt?.sales_agent_user_id || "";
    const currentBooking = appt?.booking_agent_user_id || "";

    const body = {};
    // Only fields that HAVE a current actor are sent: WP-14C grants no
    // unassignment, and an Appointment with no current actor is not something
    // this dialog can assign into.
    if (currentSales && reassignSalesId) {
      body.sales_agent_user_id = { from: currentSales, to: reassignSalesId };
    }
    if (currentBooking && reassignBookingId) {
      body.booking_agent_user_id = { from: currentBooking, to: reassignBookingId };
    }
    if (Object.keys(body).length === 0) {
      setReassignOpen(false);
      return;
    }

    setReassignSaving(true);
    let applied = false;
    try {
      const res = await api.post(`/appointments/${appointmentId}/reassign`, body);
      applied = true;
      setAppt(res.data);
      setReassignOpen(false);
      toast({ title: "Reassigned" });
      invalidateAppointmentsCache();

      // The handoff may have moved this Appointment out of the caller's own
      // record scope -- expected for a Manager who assigned it to someone they
      // do not manage. Confirm the success, then leave: re-reading would 404,
      // and that 404 is a scope transition, not a failure.
      try {
        await api.get(`/appointments/${appointmentId}`);
      } catch (err) {
        if (err?.response?.status === 404) {
          toast({
            title: "Reassigned",
            description: "This Appointment is no longer in your scope.",
          });
          navigate("/appointments");
        }
      }
    } catch (err) {
      // The write already succeeded; anything failing after it -- a cache
      // invalidation, a refetch -- must NOT be reported as "could not
      // reassign". Telling someone their change failed when it landed is
      // worse than telling them nothing, because the obvious response is to
      // do it again.
      if (applied) {
        setReassignOpen(false);
        setReassignSaving(false);
        return;
      }
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        // Stale snapshot. Never auto-retry -- that would defeat the very
        // precondition that caught this.
        toast({
          title: "Assignment changed",
          description:
            "Someone else changed this Appointment. Refreshing to show the current assignment.",
          variant: "destructive",
        });
        await loadAppointment();
        setReassignOpen(false);
      } else if (status === 500 && detail?.completion === "uncertain") {
        toast({
          title: "Possibly applied",
          description: detail.message || "Refreshing to show the current assignment.",
          variant: "destructive",
        });
        await loadAppointment();
        setReassignOpen(false);
      } else {
        toast({
          title: "Could not reassign",
          description:
            typeof detail === "string" ? detail : "Please check the selection and try again.",
          variant: "destructive",
        });
      }
    } finally {
      setReassignSaving(false);
    }
  }, [
    appt?.sales_agent_user_id,
    appt?.booking_agent_user_id,
    reassignSalesId,
    reassignBookingId,
    appointmentId,
    navigate,
    loadAppointment,
    invalidateAppointmentsCache,
    toast,
  ]);



  // Keep Add Quote modal prospect selector in sync with the currently selected prospect
  useEffect(() => {
    if (!quoteTemplateOpen) return;
    if (activeProspectId) setSelectedQuoteProspectId(activeProspectId);
  }, [activeProspectId, quoteTemplateOpen]);


  useEffect(() => {
    loadAppointment();
  }, [loadAppointment]);

  useEffect(() => {
    const leadId = appt?.lead_id;
    if (!leadId) {
      setClientCreateDate(null);
      setFirstAppointmentCreatedAt(null);
      return;
    }
    const loadRelatedDates = async () => {
      try {
        const res = await api.get(`/clients/by-lead/${leadId}`);
        setClientCreateDate(res.data?.created_at || null);
      } catch {
        setClientCreateDate(null);
      }

      try {
        const apptRes = await api.get("/appointments", {
          params: {
            filters: JSON.stringify([{ field: "lead_id", operator: "equals", value: leadId }]),
            sort_by: "created_at",
            sort_dir: "asc",
            limit: 1,
            offset: 0,
          },
        });
        const first = apptRes.data?.items?.[0];
        setFirstAppointmentCreatedAt(first?.created_at || null);
      } catch {
        setFirstAppointmentCreatedAt(null);
      }
    };
    loadRelatedDates();
  }, [appt?.lead_id]);

  useEffect(() => {
    if (!selectedDiscoveryRunId) {
      setSelectedDiscoveryRunId("all");
      return;
    }
    if (selectedDiscoveryRunId !== "all" && discoveryRuns.length && !discoveryRuns.some((run) => run.id === selectedDiscoveryRunId)) {
      setSelectedDiscoveryRunId("all");
    }
  }, [discoveryRuns, selectedDiscoveryRunId]);

  useEffect(() => {
    if (!quoteTemplateOpen) return;

    const loadTemplate = async () => {
      try {
        setQuoteTemplateLoading(true);
        setQuoteTemplateFields([]);

        if (quoteTemplateOption === "mortgage-protection") {
          const scriptsRes = await api.get("/scripts");
          const scripts = scriptsRes.data || [];
          const mpScript = scripts.find((script) => {
            const name = script?.name?.trim();
            return name === "MP - Sales Script";
          });
          if (!mpScript?.id) {
            toast({
              title: "Script not found",
              description: "MP - Sales Script was not found.",
              variant: "destructive",
            });
            setQuoteTemplateFields([]);
            setQuoteTemplateLoading(false);
            return;
          }

          const fieldsRes = await api.get(`/scripts/${mpScript.id}/quote_fields`);
          const fields = Array.isArray(fieldsRes.data) ? fieldsRes.data : fieldsRes.data?.fields || [];
          setQuoteTemplateFields(fields);
        }
      } catch (error) {
        toast({ title: "Unable to load quote template", variant: "destructive" });
      } finally {
        setQuoteTemplateLoading(false);
      }
    };

    loadTemplate();
  }, [quoteTemplateOpen, quoteTemplateOption, toast]);

  const handleAddQuoteTemplate = async () => {
    const templateFields = selectedTemplate?.fields || quoteTemplateFields;

    if (!Array.isArray(templateFields) || !templateFields.length) {
      toast({ title: "Quote template fields are still loading", variant: "destructive" });
      return;
    }

    const withRequiredFields = (() => {
      const orderedTemplateFields = [...templateFields]
        .map((field, index) => ({ ...field, __index: index }))
        .sort(
          (a, b) =>
            (a.sort_order ?? a.__index ?? 0) - (b.sort_order ?? b.__index ?? 0),
        )
        .map(({ __index, ...field }) => field);
      const list = [...orderedTemplateFields];
      const hasCompany = list.some((f) => isCompanyQuoteField(f?.label));
      const hasProduct = list.some((f) => isProductQuoteField(f?.label));
      const hasIssueAge = list.some((f) => isIssueAgeQuoteField(f?.label));
      if (!hasCompany) {
        list.unshift({ id: "company", label: "Company", field_type: "dropdown", dropdown_options: [] });
      }
      if (!hasProduct) {
        list.push({ id: "product", label: "Product", field_type: "dropdown", dropdown_options: [] });
      }
      if (!hasIssueAge) {
        const issueAgeField = { id: "issue_age", label: "Issue Age", field_type: "dropdown", dropdown_options: [] };
        const productIndex = list.findIndex((field) => isProductQuoteField(field?.label));
        if (productIndex >= 0) {
          list.splice(productIndex + 1, 0, issueAgeField);
        } else {
          list.push(issueAgeField);
        }
      }
      return list;
    })();

    const manualId = `manual-${Date.now()}`;
    const manualQuote = {
      is_manual: true,
      id: manualId,
      prospect_id: selectedQuoteProspectId,
      prospect_name: selectedQuoteProspectName,
      lead_id: selectedQuoteLeadId,
      playbook_name: selectedTemplate?.name || (quoteTemplateOption === "mortgage-protection" ? "Mortgage Protection" : "Quote"),
      quote_rows: withRequiredFields.map((field) => {
        const label = getQuoteFieldLabel(field);
        const forceDropdown = isCompanyQuoteField(label) || isProductQuoteField(label) || isIssueAgeQuoteField(label);
        return {
        quote_field_id: field.id || field.key || field.label,
        label: field.label,
        kind: field.kind || "input",
        field_type: forceDropdown ? "dropdown" : field.field_type || "text",
        template_options: forceDropdown ? [] : field.options || field.dropdown_options || [],
        options: { 1: "", 2: "", 3: "" },
        };
      }),
    };

    // Persist to backend so it survives refresh and can be edited/saved.
    try {
      const created = await api.post(`/appointments/${appointmentId}/manual_quotes`, {
        lead_id: selectedQuoteLeadId,
        household_id: appt?.household_id || null,
        prospect_id: selectedQuoteProspectId,
        prospect_name: selectedQuoteProspectName,
        playbook_name: selectedTemplate?.name || (quoteTemplateOption === "mortgage-protection" ? "Mortgage Protection" : "Quote"),
        quote_rows: manualQuote.quote_rows,
      });
      const saved = created.data || {};
      const savedId = saved.id || manualId;
      setManualQuotes((prev) => [...prev, { ...manualQuote, id: savedId }]);
      setOpenQuoteProspectId(saved.prospect_id || selectedQuoteProspectId);
    } catch {
      setManualQuotes((prev) => [...prev, manualQuote]);
      setOpenQuoteProspectId(selectedQuoteProspectId);
    }

    // Collapse any open select dropdowns by closing edit mode.
    setQuotesEditMode(false);

    setQuoteTemplateOpen(false);
  };

  const updateManualQuoteValue = (manualQuoteId, quoteFieldId, optionIndex, value) => {
    const opt = Number(optionIndex);
    if (!manualQuoteId || !quoteFieldId || ![1, 2, 3].includes(opt)) return;

    setManualQuotes((prev) =>
      (prev || []).map((q) => {
        if (!q.is_manual || q.id !== manualQuoteId) return q;
        let isCompanyUpdate = false;
        let isProductUpdate = false;
        const nextRows = (q.quote_rows || []).map((r) => {
          const rid = r.quote_field_id || r.id || r.label;
          if (rid !== quoteFieldId) return r;
          const label = getQuoteFieldLabel(r);
          if (isCompanyQuoteField(label)) {
            isCompanyUpdate = true;
          }
          if (isProductQuoteField(label)) {
            isProductUpdate = true;
          }
          return {
            ...r,
            options: {
              ...(r.options || {}),
              [opt]: value,
            },
          };
        });

        if (!isCompanyUpdate && !isProductUpdate) return { ...q, quote_rows: nextRows };

        const clearedRows = nextRows.map((r) => {
          const label = getQuoteFieldLabel(r);
          if (isCompanyUpdate && !isProductQuoteField(label) && !isIssueAgeQuoteField(label)) return r;
          if (!isCompanyUpdate && isProductUpdate && !isIssueAgeQuoteField(label)) return r;
          return {
            ...r,
            options: {
              ...(r.options || {}),
              [opt]: "",
            },
          };
        });

        return { ...q, quote_rows: clearedRows };
      })
    );
    setQuotesDirty(true);
  };


  const handleSaveProspectDetails = async () => {
    if (!appt?.lead_id) {
      toast({ title: "Lead not found", variant: "destructive" });
      return;
    }

    const overrides = prospectContactOverrides?.[activeProspectId] || {};

    const patch = {
      full_name: overrides.name ?? undefined,
      phone_number: overrides.phone ?? undefined,
      email: overrides.email ?? undefined,
      address: overrides.address ?? undefined,
      city: overrides.city ?? undefined,
      state: overrides.state ?? undefined,
      zip: overrides.zip ?? undefined,
      dob: typeof overrides.dob === "string" ? normalizeDateInput(overrides.dob) : undefined,
      household_id: appt?.household_id ?? undefined,
    };

    // Remove undefined keys (so we don't accidentally blank fields)
    Object.keys(patch).forEach((k) => {
      if (patch[k] === undefined) delete patch[k];
    });

    // Ensure we always send name & dob when saving, even if the user didn't touch those fields
    // (so UI-derived values persist after refresh).
    if (patch.full_name === undefined) {
      const nameValue = String(activeProspectContact?.name || "").trim();
      if (nameValue) patch.full_name = nameValue;
    }
    if (patch.dob === undefined) {
      const dobValue = normalizeDateInput(activeProspectContact?.dob || "");
      if (dobValue) patch.dob = dobValue;
    }

    setIsProspectSaving(true);
    try {
      const isPrimary = activeProspect?.lead_id
        ? activeProspect.lead_id === appt.lead_id
        : activeProspectIndex === 0;
      let targetLeadId = activeProspect?.lead_id || appt.lead_id;

      if (!isPrimary) {
        targetLeadId = activeProspect?.lead_id || (await ensureSecondaryLead()) || targetLeadId;
      }

      const leadPayload = {
        ...patch,
        full_name: patch.full_name ?? undefined,
      };

      const res = await api.put(`/leads/${targetLeadId}`, leadPayload);

      // Refresh appointment lead_snapshot to reflect latest data in UI.
      // NOTE: lead_snapshot is for the appointment's primary lead; secondary lead edits are visible
      // via household lead lookup (displayLead).
      if (isPrimary) {
        setAppt((prev) => {
          if (!prev) return prev;
          const nextLead = { ...(prev.lead_snapshot || {}), ...(res.data || {}) };
          return { ...prev, lead_snapshot: nextLead };
        });
      } else {
        setHouseholdLeads((prev) => (prev || []).map((l) => (l.id === targetLeadId ? { ...l, ...(res.data || {}) } : l)));
      }

      if (activeProspectId && activeProspectId !== "lead-default") {
        const runProspectName = String(
          overrides.name ?? res?.data?.full_name ?? activeProspectContact?.name ?? activeProspect?.name ?? ""
        ).trim();

        if (runProspectName) {
          try {
            await api.put(`/run-prospects/${activeProspectId}`, {
              name: runProspectName,
              lead_id: targetLeadId,
            });
          } catch (error) {
            console.warn("Unable to update run prospect", error);
          }
        }
      }

      let updatedProspects = null;
      if (activeProspectId && Array.isArray(prospects)) {
        updatedProspects = prospects.map((p) => {
          if (p.id !== activeProspectId) return p;
          return {
            ...p,
            name: overrides.name ?? p.name,
            lead_id: targetLeadId ?? p.lead_id,
            dob: typeof overrides.dob === "string" ? normalizeDateInput(overrides.dob) : p.dob,
          };
        });
      } else if (activeProspectId && activeProspect) {
        updatedProspects = [
          {
            ...activeProspect,
            name: overrides.name ?? activeProspect.name,
            lead_id: targetLeadId ?? activeProspect.lead_id,
            dob: typeof overrides.dob === "string" ? normalizeDateInput(overrides.dob) : activeProspect.dob,
          },
        ];
      }

      if (updatedProspects) {
        setProspects(updatedProspects);
      }

      const appointmentPatch = {};
      if (updatedProspects) {
        appointmentPatch.prospects = updatedProspects;
      }
      if (isPrimary && res?.data) {
        appointmentPatch.lead_snapshot = { ...(appt?.lead_snapshot || {}), ...(res.data || {}) };
      }

      // P4-DEBT-B: the obsolete actor PUT is gone.
      //
      // This sent `sales_agent_user_id` / `booking_agent_user_id` and their
      // names to `PUT /appointments/{id}`, gated on the deprecated global
      // `users.role` read out of localStorage. `AppointmentUpdate` declares no
      // actor field and runs `extra="ignore"` (WP-14B, because actor identity
      // decides record scope), so every one of those four keys was dropped and
      // the request persisted nothing but a fresh `updated_at`. The save still
      // reported success and the pane rendered the locally chosen name, so the
      // change looked applied until the next read restored the server value.
      //
      // Reassignment is `POST /appointments/{id}/reassign` (WP-14C): capability
      // gate, server-side eligibility, compare-and-set, atomic multi-actor
      // update, server-derived names and a purpose-built audit. It is reachable
      // from the Reassign button on this page.


      if (appointmentId && Object.keys(appointmentPatch).length > 0) {
        await api.put(`/appointments/${appointmentId}`, appointmentPatch);
      }



      toast({ title: "Saved" });
      setIsProspectEditing(false);
      
      // REACT QUERY: Invalidate appointments list cache after saving prospect details
      invalidateAppointmentsCache();

      if (activeProspectId) {
        setProspectContactOverrides((prev) => ({
          ...(prev || {}),
          [activeProspectId]: {
            ...(prev?.[activeProspectId] || {}),
            ...overrides,
            dob: typeof overrides.dob === "string" ? normalizeDateInput(overrides.dob) : prev?.[activeProspectId]?.dob,
          },
        }));
      }
    } catch (e) {
      toast({ title: "Unable to save prospect details", variant: "destructive" });
    } finally {
      setIsProspectSaving(false);
    }
  };

  const handleSaveManualQuotes = async () => {
    const manual = (manualQuotes || []).filter((q) => q && q.is_manual && q.id);
    if (!manual.length) {
      setQuotesDirty(false);
      setQuotesEditMode(false);
      return;
    }

    setIsQuotesSaving(true);
    try {
      await Promise.all(
        manual.map((q) =>
          api.put(`/appointments/${appointmentId}/manual_quotes/${q.id}`, {
            lead_id: q.lead_id || null,
            household_id: appt?.household_id || null,
            prospect_id: q.prospect_id || null,
            prospect_name: q.prospect_name || null,
            playbook_name: q.playbook_name || null,
            quote_rows: q.quote_rows || [],
          })
        )
      );

      toast({ title: "Saved" });
      setQuotesDirty(false);
      setQuotesEditMode(false);
      setOpenQuoteProspectId(null);
      // Keep local prospect edits without forcing a full reload
    } catch {
      toast({ title: "Unable to save quotes", variant: "destructive" });
    } finally {
      setIsQuotesSaving(false);
    }
  };

  const openDeleteConfirm = (payload) => {
    setDeleteTarget(payload);
    setDeleteConfirmOpen(true);
  };

  const deleteManualQuote = async (manualQuoteId, panelId) => {
    if (!manualQuoteId) return;
    try {
      await api.delete(`/appointments/${appointmentId}/manual_quotes/${manualQuoteId}`);
      setManualQuotes((prev) => (prev || []).filter((q) => q.id !== manualQuoteId));
      if (openQuoteProspectId === panelId) {
        setOpenQuoteProspectId(null);
      }
      toast({ title: "Quote deleted" });
    } catch (e) {
      toast({ title: "Unable to delete quote", variant: "destructive" });
    }
  };

  const deleteRunQuote = async (panelId) => {
    if (!panelId) return;
    try {
      const nextHidden = Array.from(new Set([...(hiddenQuotePanels || []), panelId]));
      const res = await api.put(`/appointments/${appointmentId}`, { hidden_quote_panels: nextHidden });
      setAppt((prev) => (prev ? { ...prev, hidden_quote_panels: nextHidden } : prev));
      if (res?.data?.id) {
        setAppt((prev) => (prev ? { ...prev, ...res.data } : prev));
      }
      toast({ title: "Quote deleted" });
    } catch (e) {
      toast({ title: "Unable to delete quote", variant: "destructive" });
    }
  };

  const deleteAppointment = async (item) => {
    if (!item?.id) return;
    try {
      await api.delete(`/appointments/${item.id}`);
      setHistory((prev) => (prev || []).filter((apptItem) => apptItem.id !== item.id));
      toast({ title: "Appointment deleted" });
      // REACT QUERY: Invalidate the appointments list cache
      invalidateAppointmentsCache();
      if (item.id === appointmentId) {
        navigate(buildPathWithCurrentTenant("/app/appointments"));
      }
    } catch (e) {
      toast({ title: "Unable to delete appointment", variant: "destructive" });
    }
  };

  const handleDeleteManualQuote = (manualQuoteId, panelId) => {
    if (!manualQuoteId) return;
    openDeleteConfirm({ kind: "manual_quote", manualQuoteId, panelId });
  };

  const handleHideRunQuote = (panelId) => {
    if (!panelId) return;
    openDeleteConfirm({ kind: "run_quote", panelId });
  };

  const handleDeleteAppointment = (item) => {
    if (!item?.id) return;
    openDeleteConfirm({ kind: "appointment", item });
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleteBusy) return;
    setDeleteBusy(true);
    try {
      if (deleteTarget.kind === "manual_quote") {
        await deleteManualQuote(deleteTarget.manualQuoteId, deleteTarget.panelId);
      } else if (deleteTarget.kind === "run_quote") {
        await deleteRunQuote(deleteTarget.panelId);
      } else if (deleteTarget.kind === "appointment") {
        await deleteAppointment(deleteTarget.item);
      }
    } finally {
      setDeleteBusy(false);
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    }
  };

  const handleSaveDiscoveryOverrides = async () => {
    if (!appt?.id || selectedDiscoveryRunId === "all") {
      toast({ title: "Cannot save discovery overrides for 'All Runs' view", variant: "destructive" });
      return;
    }

    setIsDiscoverySaving(true);
    try {
      const entries = Object.entries(discoveryEdits).map(([key, values]) => {
        const [subjectId, fieldKey] = key.split("::");
        return {
          subject_id: subjectId || "household",
          field_key: fieldKey,
          values: Array.isArray(values) ? values : [values],
        };
      });

      await api.post(`/appointments/${appointmentId}/discovery_overrides`, {
        run_id: selectedDiscoveryRunId,
        entries,
      });

      toast({ title: "Discovery overrides saved" });
      setDiscoveryDirty(false);
      setIsDiscoveryEditing(false);
      await loadAppointment();
    } catch (error) {
      toast({ title: "Unable to save discovery overrides", variant: "destructive" });
    } finally {
      setIsDiscoverySaving(false);
    }
  };

  // The full-page skeleton belongs to HYDRATION -- there is no Appointment to
  // render yet -- not to every loadAppointment call.
  //
  // `loading` alone meant any refresh replaced the whole page, dialog subtree
  // included. Submitting an Appointment Summary does exactly that: onSubmitted
  // starts loadAppointment, whose setLoading(true) runs before its first await
  // and lands in the same batch as the setFollowUpOpen(true) that follows it,
  // so the scheduler the user just asked for could not render until five or
  // more serial GETs had finished. Reschedule (#627) and the follow-up
  // fallback (#645) refresh through the same call and paid the same cost.
  //
  // Keyed on the Appointment ACTUALLY RENDERED, not on a boolean, so a route
  // change still hydrates: `appt` then holds the previous Appointment while
  // the URL names the next one, and showing that under the new route would be
  // worse than a skeleton. Only a refresh of the Appointment already on
  // screen repaints in place.
  //
  // Nothing else moves. `loading` still spans the whole fetch, the error
  // branch below is untouched, and a failed refresh still clears `appt` and
  // falls through to it exactly as before.
  const hydratingAppointment = loading && appt?.id !== appointmentId;

  if (hydratingAppointment) {
    return (
      <div data-testid="appointment-details-loading">
        <DetailPageSkeleton variant="appointment" />
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="appointment-details-error">
        <div className="rounded-2xl bg-white/5 px-4 py-6 text-sm text-rose-200/80 ring-1 ring-rose-400/30">
          {error}
        </div>
      </div>
    );
  }

  if (!appt) {
    return <div className="text-sm text-slate-200/70">Appointment not found.</div>;
  }

  return (
    <div className="space-y-6 pb-10" data-testid="appointment-details-page">
      <FireworksCanvas active={showFireworks} onComplete={() => setShowFireworks(false)} />
      <SoldDialog
        open={soldOpen}
        onOpenChange={(next) => {
          setSoldOpen(next);
          if (!next) setSoldContext(null);
        }}
        appointmentId={appointmentId}
        prefill={soldContext?.prefill}
        quoteRef={soldContext?.quoteRef}
        onSold={(payload) => {
          setAppt((prev) => {
            if (!prev) return prev;
            const existing = Array.isArray(prev.sold_payloads)
              ? prev.sold_payloads
              : prev.sold_payload
                ? [prev.sold_payload]
                : [];
            return { ...prev, status: "Sold", sold_payload: payload, sold_payloads: [...existing, payload] };
          });
          setShowFireworks(true);
          // REACT QUERY: Invalidate appointments and clients list cache after sale
          invalidateAppointmentsCache();
          queryClient.invalidateQueries({
            queryKey: queryKeys.clients.all(selectedTenantId),
          });
        }}
      />
      <AddProspectDialog
        open={addProspectOpen}
        onOpenChange={setAddProspectOpen}
        householdId={appt?.household_id}
        onAdded={(p) =>
          setProspects((prev) => [
            ...prev,
            { id: `local_${Date.now()}`, name: p.name, lead_id: p.lead_id },
          ])
        }
      />

      <SetAppointmentDialog
        lead={leadForScheduling}
        open={Boolean(rescheduleAppt)}
        onOpenChange={(next) => {
          if (!next) setRescheduleAppt(null);
        }}
        mode="schedule"
        appointmentId={rescheduleAppt?.id || null}
        // The row itself, so the dialog can open on the time this Appointment
        // already has and keep its duration. Identity still comes from
        // `appointmentId` above -- this adds data, not a second id source.
        appointment={rescheduleAppt}
        // Re-read after a successful reschedule. `appt` and `history` are
        // local state owned here, and the dialog discards its PUT response, so
        // without this the row kept rendering the pre-edit time until a
        // browser refresh -- the Appointment was already persisted, the page
        // just never asked again. The dialog has always invoked onConverted on
        // this path; only the handler was missing.
        //
        // Refetching rather than merging the response is deliberate: the list
        // is ordered by the backend (start_at desc), so a reschedule can move
        // a row, and the canonical loader refreshes the route Appointment and
        // the lead-scoped history together. This is what summary submission
        // and discovery-override saves already do.
        onConverted={async () => {
          await loadAppointment();
        }}
      />

      {/* NEW appointment for this prospect. Creates a sibling Appointment and
          never reschedules the one being viewed. */}
      <SetAppointmentDialog
        lead={leadForScheduling}
        open={followUpOpen}
        onOpenChange={setFollowUpOpen}
        mode="follow_up"
        appointmentId={followUpParentId || appointmentId}
        // Navigating to the follow-up refreshes everything, because the route
        // param changes and loadAppointment re-runs for the new Appointment.
        //
        // Without an id there is nothing to navigate to, and this used to do
        // nothing at all: the dialog had already fired "Appointment booked"
        // and closed, so a persisted follow-up left the user on an unchanged
        // page whose history did not list it. `appt`, the Lead and the
        // lead-scoped history are local state owned here and are only ever
        // filled by loadAppointment, so without this they stay pre-creation
        // until some other event refetches them.
        //
        // The current backend cannot produce that -- AppointmentPublic.id is
        // required, so a 2xx always carries one -- which is exactly why the
        // branch is worth closing rather than leaving as a silent no-op: an
        // unexpected response shape would otherwise read as success.
        //
        // Refetching rather than appending the created row matches the
        // reschedule handler above: the list is backend-ordered, so a new
        // Appointment can land anywhere in it.
        onConverted={async (createdId) => {
          if (createdId) {
            navigate(buildPathWithCurrentTenant(`/app/appointments/${createdId}`));
            return;
          }
          await loadAppointment();
        }}
      />

      <StartRunScriptDialog
        open={scriptPickerOpen}
        onOpenChange={setScriptPickerOpen}
        leadId={appt?.lead_id || null}
        prospect1Name={displayLead?.full_name || ""}
        appointmentId={appointmentId}
      />
      <div className="flex items-center justify-between gap-3">
        <Button
          type="button"
          variant="secondary"
          className="h-9 rounded-full bg-indigo-500/15 px-4 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/25"
          onClick={() => navigate(getBreadcrumbBackPath("/app/appointments"))}
          data-testid="appointment-back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            className="h-9 rounded-full bg-indigo-500/90 px-4 text-white hover:bg-indigo-500"
            onClick={() => setScriptPickerOpen(true)}
            data-testid="appointment-start-run-button"
          >
            <Play className="mr-2 h-4 w-4" /> Start Run
          </Button>
            {/* P4-WP-14C: an EXPLICIT action, deliberately not part of Edit.
                Rendered only when the backend confirmed the capability, so a
                Sales or Booking Agent never sees a control they cannot use. */}
            {canReassign ? (
              <Button
                type="button"
                variant="outline"
                className="h-9 rounded-full px-4"
                onClick={openReassign}
                data-testid="appointment-reassign-button"
              >
                <Users className="mr-2 h-4 w-4" /> Reassign
              </Button>
            ) : null}
        </div>
      </div>

      <AppointmentProspectDetailsSection
        segmentedToggleWrapper={segmentedToggleWrapper}
        segmentedToggleBase={segmentedToggleBase}
        segmentedToggleActive={segmentedToggleActive}
        hasProspectToggle={hasProspectToggle}
        activeProspectIndex={activeProspectIndex}
        primaryProspect={primaryProspect}
        secondaryProspect={secondaryProspect}
        setActiveProspectId={setActiveProspectId}
        accentIconButtonClass={accentIconButtonClass}
        saveIconButtonClass={saveIconButtonClass}
        setAddProspectOpen={setAddProspectOpen}
        isProspectEditing={isProspectEditing}
        setIsProspectEditing={setIsProspectEditing}
        isProspectSaving={isProspectSaving}
        handleSaveProspectDetails={handleSaveProspectDetails}
        prospects={prospects}
        activeProspectContact={activeProspectContact}
        displayLead={displayLead}
        updateProspectContact={updateProspectContact}
        prospectDob={prospectDob}
        appt={appt}
        fmtDateTime={fmtDateTime}
      />

      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent data-testid="appointment-reassign-dialog">
          <DialogHeader>
            <DialogTitle>Reassign Appointment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {appt?.sales_agent_user_id ? (
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Sales Agent</label>
                <SingleSelectFilter
                  placeholder="Select sales agent"
                  value={reassignSalesId}
                  onChange={setReassignSalesId}
                  options={(assignableActors.sales || []).map((u) => ({
                    value: u.user_id,
                    label: u.name || u.email,
                  }))}
                />
              </div>
            ) : null}
            {appt?.booking_agent_user_id ? (
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Booking Agent</label>
                <SingleSelectFilter
                  placeholder="Select booking agent"
                  value={reassignBookingId}
                  onChange={setReassignBookingId}
                  options={(assignableActors.booking || []).map((u) => ({
                    value: u.user_id,
                    label: u.name || u.email,
                  }))}
                />
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setReassignOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitReassign}
              disabled={reassignSaving}
              data-testid="appointment-reassign-submit"
            >
              {reassignSaving ? "Reassigning..." : "Reassign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AppointmentSummaryDialog
        open={summaryOpen}
        onOpenChange={setSummaryOpen}
        appointment={summaryAppt}
        readOnly={Boolean(summaryAppt?.summary_payload)}
        onScheduleAfterSubmit={(followUpAppointmentId) => {
          // The follow-up the summary created is a DIFFERENT Appointment.
          // This previously opened the scheduler still bound to the
          // route-param (parent) id, so the user rescheduled the wrong
          // record. The summary response already carries the new id in
          // summary_payload.follow_up_appointment_id, so go to it rather than
          // rediscovering it -- and it is scheduled at creation, so no second
          // scheduling step is needed.
          if (followUpAppointmentId) {
            navigate(
              buildPathWithCurrentTenant(`/app/appointments/${followUpAppointmentId}`)
            );
            return;
          }
          // No follow-up was created (no time collected). The user asked to
          // schedule the NEXT appointment, so open creation -- never the
          // rescheduler for the appointment they just completed.
          //
          // The raw setter on purpose: `followUpParentId` already names the
          // row whose Summary this was, and the scheduler must book a
          // follow-up of THAT Appointment even when the route names another.
          setFollowUpOpen(true);
        }}
        onSubmitted={async () => {
          setSummaryOpen(false);
          setSummaryAppt(null);
          await loadAppointment();
        }}
      />

      <Dialog
        open={deleteConfirmOpen}
        onOpenChange={(open) => {
          setDeleteConfirmOpen(open);
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent className="glass border-white/10" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-100" data-testid="delete-confirm-title">
              Delete {deleteTargetLabel}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-200/70" data-testid="delete-confirm-description">
            This action cannot be undone.
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              className="rounded-full bg-white/10 text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
              onClick={() => setDeleteConfirmOpen(false)}
              data-testid="delete-confirm-cancel"
            >
              Cancel
            </Button>
            <Button
              className="rounded-full bg-rose-500/90 text-white hover:bg-rose-500"
              onClick={confirmDelete}
              disabled={deleteBusy || !deleteTarget}
              data-testid="delete-confirm-confirm"
            >
              {deleteBusy ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddQuoteDialog
        open={quoteTemplateOpen}
        onOpenChange={setQuoteTemplateOpen}
        saveSelectTriggerClass={saveSelectTriggerClass}
        selectedQuoteProspectId={selectedQuoteProspectId}
        setSelectedQuoteProspectId={setSelectedQuoteProspectId}
        quoteProspectOptions={quoteProspectOptions}
        quoteTemplateOption={quoteTemplateOption}
        setQuoteTemplateOption={setQuoteTemplateOption}
        quoteTemplates={quoteTemplates}
        quoteTemplateLoading={quoteTemplateLoading}
        selectedTemplate={selectedTemplate}
        quoteTemplateFields={quoteTemplateFields}
        navigate={navigate}
        location={location}
        handleAddQuoteTemplate={handleAddQuoteTemplate}
        setQuoteTemplates={setQuoteTemplates}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <AppointmentHistorySection
          accentIconButtonClass={accentIconButtonClass}
          onRescheduleAppointment={setRescheduleAppt}
          // The "+" is the section-level creation action: it books another
          // Appointment for this prospect from the page being viewed, so its
          // parent is the route Appointment. Stated by clearing the override
          // rather than left to whatever a previous opener set.
          setFollowUpOpen={(next) => {
            if (next) setFollowUpParentId(null);
            setFollowUpOpen(next);
          }}
          setAppointmentsEditMode={setAppointmentsEditMode}
          appointmentHistory={appointmentHistory}
          appointmentsEditMode={appointmentsEditMode}
          canDeleteAppointment={canDeleteAppointment}
          handleDeleteAppointment={handleDeleteAppointment}
          fmtDateTime={fmtDateTime}
          // Opening a row's Summary also names the Appointment a Yes answer
          // would schedule after, recorded HERE rather than at submit time:
          // onSubmitted clears `summaryAppt` before onScheduleAfterSubmit runs.
          setSummaryAppt={(row) => {
            setSummaryAppt(row);
            setFollowUpParentId(row?.id || null);
          }}
          setSummaryOpen={setSummaryOpen}
        />

        <AppointmentQuotesSection
          accentIconButtonClass={accentIconButtonClass}
          saveIconButtonClass={saveIconButtonClass}
          quotesEditMode={quotesEditMode}
          quotesDirty={quotesDirty}
          isQuotesSaving={isQuotesSaving}
          setQuoteTemplateOpen={setQuoteTemplateOpen}
          setQuotesEditMode={setQuotesEditMode}
          setQuotesDirty={setQuotesDirty}
          handleSaveManualQuotes={handleSaveManualQuotes}
          quoteProspects={quoteProspects}
          hiddenQuotePanels={hiddenQuotePanels}
          openQuoteProspectId={openQuoteProspectId}
          setOpenQuoteProspectId={setOpenQuoteProspectId}
          soldPayloads={soldPayloads}
          quotes={quotes}
          quoteProspectLeadMap={quoteProspectLeadMap}
          buildSoldPrefill={buildSoldPrefill}
          setSoldContext={setSoldContext}
          setSoldOpen={setSoldOpen}
          handleDeleteManualQuote={handleDeleteManualQuote}
          handleHideRunQuote={handleHideRunQuote}
          updateManualQuoteValue={updateManualQuoteValue}
        />
      </div>

      <AppointmentDiscoverySection
        appointmentId={appointmentId}
        toast={toast}
        selectedDiscoveryRunId={selectedDiscoveryRunId}
        setSelectedDiscoveryRunId={setSelectedDiscoveryRunId}
        discoveryRunOptions={discoveryRunOptions}
        hasProspectToggle={hasProspectToggle}
        segmentedToggleWrapper={segmentedToggleWrapper}
        segmentedToggleBase={segmentedToggleBase}
        segmentedToggleActive={segmentedToggleActive}
        discoveryProspectIndex={discoveryProspectIndex}
        primaryProspect={primaryProspect}
        secondaryProspect={secondaryProspect}
        setDiscoveryProspectId={setDiscoveryProspectId}
        isDiscoveryEditing={isDiscoveryEditing}
        setIsDiscoveryEditing={setIsDiscoveryEditing}
        discoveryDirty={discoveryDirty}
        setDiscoveryDirty={setDiscoveryDirty}
        discoveryEdits={discoveryEdits}
        setDiscoveryEdits={setDiscoveryEdits}
        handleSaveDiscoveryOverrides={handleSaveDiscoveryOverrides}
        isDiscoverySaving={isDiscoverySaving}
        accentIconButtonClass={accentIconButtonClass}
        saveIconButtonClass={saveIconButtonClass}
        discovery={discovery}
        discoverySectionsOpen={discoverySectionsOpen}
        setDiscoverySectionsOpen={setDiscoverySectionsOpen}
        discoveryProspect={discoveryProspect}
        discoveryFields={discoveryFields}
        discoveryValuesByKey={discoveryValuesByKey}
        formatDiscoveryValue={formatDiscoveryValue}
      />

      <AppointmentLeadDetailsSection
        lead={lead}
        fmtDateTime={fmtDateTime}
        clientCreateDate={clientCreateDate}
        appointmentCreateDate={firstAppointmentCreatedAt}
      />
    </div>
  );
}
